import React, {useEffect, useCallback, useContext} from 'react';
import {AlertInfo, AlertWarning} from '@homeaway/react-alerts';
import {Button} from '@homeaway/react-buttons';
import {TrackingContext} from '@homeaway/react-event-tracking';
import PropTypes from 'prop-types';
import './GlobalInlineAlert.less';

const MSG_TYPE = 'inline';

// eslint-disable-next-line complexity
function useFormatContent(globalMessages) {
    const {trigger} = useContext(TrackingContext);

    // Callback invoked when the user selects the inline link
    const triggerLinkEvent = useCallback(() => {
        const alert = globalMessages.alert;
        trigger('global_message_inline_link.selected', {
            message_id: alert.id,
            message_severity: alert.severity,
            message_type: MSG_TYPE,
            target_url: alert.body.link.href,
        });
    }, [globalMessages, trigger]);

    if (globalMessages && globalMessages.alert && globalMessages.alert.body) {
        const {body} = globalMessages.alert;

        // If a link is present, add it to the end of the message content
        if (body.link && body.link.href && body.link.text.value && body.text) {
            return (
                <p>
                    <span className="GlobalInlineAlert__content">
                        {body.text.value}
                    </span>
                    <a
                        className="GlobalInlineAlert__link"
                        href={body.link.href}
                        onClick={triggerLinkEvent}
                        rel="noopener"
                        target="_blank"
                    >
                        {body.link.text.value}
                    </a>
                </p>
            );
        } else if (body.text && body.text.value) {
            return <p>{body.text.value}</p>;
        }
    }

    return null;
}

// eslint-disable-next-line complexity
function GlobalInlineAlert({globalMessages}) {
    const {trigger} = useContext(TrackingContext);

    // Code to run on component mount
    useEffect(() => {
        if (globalMessages && globalMessages.alert) {
            const alert = globalMessages.alert;
            trigger('global_message.presented', {
                message_id: alert.id,
                message_severity: alert.severity,
                message_type: MSG_TYPE,
            });
        }
        // This is intended as a one time run upon component mount so no
        // dependencies specified.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Callback invoked when the user selects the CTA button
    const triggerCTAEvent = useCallback(() => {
        const alert = globalMessages.alert;
        trigger('global_message_cta_link.selected', {
            message_id: alert.id,
            message_severity: alert.severity,
            message_type: MSG_TYPE,
            target_url: alert.action.link.href,
        });
    }, [globalMessages, trigger]);

    let content = useFormatContent(globalMessages);

    if (!globalMessages || !globalMessages.alert) {
        return null;
    }

    const {action, severity, title} = globalMessages.alert;
    const isHighSeverity = severity === 'HIGH';
    const alertChildren = (
        <>
            {content}
            {action &&
                action.link &&
                action.link.href &&
                action.link.text &&
                action.link.text.value && (
                    <div className="GlobalInlineAlert__cta">
                        <Button
                            href={action.link.href}
                            label={action.link.text.value}
                            onClick={triggerCTAEvent}
                            size="xs"
                            target="_blank"
                        />
                    </div>
                )}
        </>
    );

    return (
        <>
            {isHighSeverity && (
                <AlertWarning ariaLive={'assertive'} title={title.value}>
                    {alertChildren}
                </AlertWarning>
            )}
            {!isHighSeverity && (
                <AlertInfo subtle title={title.value}>
                    {alertChildren}
                </AlertInfo>
            )}
        </>
    );
}

GlobalInlineAlert.propTypes = {
    /** Data retrieved from the global messaging service. */
    globalMessages: PropTypes.shape({
        /** The alert content. */
        alert: PropTypes.shape({
            /** Optional call to action (CTA) button that redirects to the specified link. */
            action: PropTypes.shape({
                /** CTA button label and link. */
                link: PropTypes.shape({
                    href: PropTypes.string,
                    text: PropTypes.shape({
                        value: PropTypes.string,
                    }),
                }),
            }),
            /** Message body content. */
            body: PropTypes.shape({
                text: PropTypes.shape({
                    value: PropTypes.string,
                }),
                /** Inline link information. */
                link: PropTypes.shape({
                    href: PropTypes.string,
                    text: PropTypes.shape({
                        value: PropTypes.string,
                    }),
                }),
            }),
            /** Message ID */
            id: PropTypes.string,
            /** Message severity. 'HIGH' or 'LOW' */
            severity: PropTypes.string,
            /** The message title information. */
            title: PropTypes.shape({
                value: PropTypes.string,
            }),
        }),
    }),
};

export default GlobalInlineAlert;
